import { X } from 'lucide-react'
import type { OperatorSettings } from '../operatorSettings'

// « Paramètres Opérateur » : réglages d'affichage de cette tablette. Les boutons de calibration (Profil, Diag, Détection)
// n'apparaissent que là où les outils temporaires existent (jamais en production).

export interface SettingsDialogProps {
  settings: OperatorSettings
  calibrationTools: boolean
  onChange: (next: OperatorSettings) => void
  onClose: () => void
}

function Toggle({ label, hint, on, onToggle }: { label: string; hint: string; on: boolean; onToggle: () => void }) {
  return (
    <div className="op-settings__row">
      <span>
        <strong>{label}</strong>
        <small>{hint}</small>
      </span>
      <button type="button" role="switch" aria-checked={on} aria-label={label} className="op-switch" onClick={onToggle}>
        {on ? 'On' : 'Off'}
      </button>
    </div>
  )
}

export function SettingsDialog({ settings, calibrationTools, onChange, onClose }: SettingsDialogProps) {
  const set = (patch: Partial<OperatorSettings>) => {
    onChange({ ...settings, ...patch })
  }
  return (
    <div className="op-settings" role="dialog" aria-modal="true" aria-label="Paramètres Opérateur">
      <section className="op-settings__card">
        <header className="op-settings__head">
          <h2>Paramètres Opérateur</h2>
          <button type="button" className="op-settings__close" aria-label="Fermer les paramètres" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <p className="op-settings__note">Réglages de cette tablette seulement.</p>

        {calibrationTools ? (
          <>
            <Toggle
              label="Bouton Profil"
              hint="Profil de calibration dans l’en-tête (remplace Pause quand il est affiché)."
              on={settings.showProfileButton}
              onToggle={() => {
                set({ showProfileButton: !settings.showProfileButton })
              }}
            />
            <Toggle
              label="Bouton Diag"
              hint="Diagnostic de détection dans l’en-tête."
              on={settings.showDiagButton}
              onToggle={() => {
                set({ showDiagButton: !settings.showDiagButton })
              }}
            />
            <Toggle
              label="Bouton Détection"
              hint="Drapeaux et zones de détection sur la carte, dans l’en-tête."
              on={settings.showDetectionButton}
              onToggle={() => {
                set({ showDetectionButton: !settings.showDetectionButton })
              }}
            />
          </>
        ) : null}
        <Toggle
          label="Bouton Focus"
          hint="Bascule rapide du style du panneau dans l’en-tête."
          on={settings.showFocusButton}
          onToggle={() => {
            set({ showFocusButton: !settings.showFocusButton })
          }}
        />
        <div className="op-settings__row">
          <span>
            <strong>Numéros civiques</strong>
            <small>Taille des numéros des résidences de la mission sur la carte.</small>
          </span>
          <div className="op-settings__choice" role="group" aria-label="Taille des numéros civiques">
            <button
              type="button"
              aria-pressed={settings.civicSize === 'normal'}
              onClick={() => {
                set({ civicSize: 'normal' })
              }}
            >
              Normale
            </button>
            <button
              type="button"
              aria-pressed={settings.civicSize === 'large'}
              onClick={() => {
                set({ civicSize: 'large' })
              }}
            >
              Grande
            </button>
          </div>
        </div>
        <div className="op-settings__row">
          <span>
            <strong>Style du panneau</strong>
            <small>A : résidence en cours et liste. B : une résidence à la fois.</small>
          </span>
          <div className="op-settings__choice" role="group" aria-label="Style du panneau">
            <button
              type="button"
              aria-pressed={settings.panelStyle === '1a'}
              onClick={() => {
                set({ panelStyle: '1a' })
              }}
            >
              A
            </button>
            <button
              type="button"
              aria-pressed={settings.panelStyle === '1b'}
              onClick={() => {
                set({ panelStyle: '1b' })
              }}
            >
              B
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
